import { Spinner } from "@fluentui/react"
import {
  errorToString,
  FundingSchedule,
  FundingScheduleLabels,
  Org,
  STATE_CODES,
} from "@oneethos/shared"
import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import api from '../api-client'
import { useApi, useAppState } from "../hooks"
import { Checkbox, InputRadio, MultiSelect, PasswordInput, RoutingNumberInput, TextInput } from "./forms"
import Section from "./section"
import { ApiKeys } from "./api-keys"

type OrgAdminProps = {
  installerID: string
}

type OrgFormSectionProps = {
  org: Partial<Org>
  onChange: (org: Partial<Org>) => void
}

export const EnabledIntegrations = ({ onChange, enabledIntegrations }) => {
  const { data: integrations, fetching, error } = useApi<{ integrationId: string }[]>(`/orgs/integrations`, [])

  if (fetching) {
    return <Spinner />
  }

  return <div>
    <h5>Enabled Integrations</h5>
    {error ? <div className="alert alert-danger">{errorToString(error)}</div> : null}
    <p>
      Enabling an integration will make this Organization
      visible to Integration Admins for the Integrator
    </p>
    {integrations?.length ? <div>
      <MultiSelect
        options={integrations.reduce((acc, i) => {
          acc[i.integrationId] = i.integrationId
          return acc
        }, {})}
        values={enabledIntegrations || []}
        onChange={onChange}
      />
    </div> : <div className="well">No integrations enabled</div>}
  </div>
}

const OrgBasicInformationSection = ({ org, onChange }: OrgFormSectionProps) => (
  <div className="form-group">
    <h4>Basic Information</h4>
    <label>Tesla Certified Installer</label>
    <InputRadio
      id="tesla-yes"
      name="isTeslaCertified"
      checked={org?.isTeslaCertified === true}
      onChange={() => onChange({ ...org, isTeslaCertified: true })}
      label="Yes"
    />
    <InputRadio
      id="tesla-no"
      name="isTeslaCertified"
      checked={org?.isTeslaCertified === false}
      onChange={() => onChange({ ...org, isTeslaCertified: false })}
      label="No"
    />
    <label className="mt-3">Operating States</label>
    <MultiSelect
      options={STATE_CODES}
      values={org?.operatingStates || []}
      onChange={states => onChange({ ...org, operatingStates: states })}
      wrapStyle={{ maxHeight: '26em', overflowY: 'auto' }}
    />
  </div>
)

const OrgFundingScheduleSection = ({ org, onChange }: OrgFormSectionProps) => (
  <div className="form-group">
    <h4>Funding Schedule</h4>
    <div className="d-flex flex-column gap-2">
      <InputRadio
        id="funding-schedule-50-50"
        name="fundingSchedule"
        checked={org?.fundingSchedule === FundingSchedule.FiftyFifty}
        onChange={() => onChange({ ...org, fundingSchedule: FundingSchedule.FiftyFifty })}
        label={FundingScheduleLabels[FundingSchedule.FiftyFifty]}
      />
      <InputRadio
        id="funding-schedule-20-80"
        name="fundingSchedule"
        checked={org?.fundingSchedule === FundingSchedule.TwentyEighty}
        onChange={() => onChange({ ...org, fundingSchedule: FundingSchedule.TwentyEighty })}
        label={FundingScheduleLabels[FundingSchedule.TwentyEighty]}
      />
      <InputRadio
        id="funding-schedule-70-30"
        name="fundingSchedule"
        checked={org?.fundingSchedule === FundingSchedule.SeventyThirty}
        onChange={() => onChange({ ...org, fundingSchedule: FundingSchedule.SeventyThirty })}
        label={FundingScheduleLabels[FundingSchedule.SeventyThirty]}
      />
      <InputRadio
        id="funding-schedule-100"
        name="fundingSchedule"
        checked={org?.fundingSchedule === FundingSchedule.OneHundred}
        onChange={() => onChange({ ...org, fundingSchedule: FundingSchedule.OneHundred })}
        label={FundingScheduleLabels[FundingSchedule.OneHundred]}
      />
    </div>
  </div>
)

const OrgIntegrationSection = ({ org, onChange }: OrgFormSectionProps) => (
  <>
    <div className="form-group">
      <h4>Integration</h4>
      <Checkbox
        label="API Enabled"
        value={org?.apiEnabled}
        onChange={ev => onChange({ ...org, apiEnabled: ev.target.checked })}
      />
    </div>
    <div className="form-group">
      <div className="alert alert-secondary mb-1">
        <div>Setting the Org as an Integration partner will:</div>
        <ul className="mb-0">
          <li>
            Enable the Org to display as an integration
            in org settings (for support users only)
          </li>
          <li>Enable support users to associate other orgs with this org</li>
          <li>Enable <b>Integration Admin</b> roles for this org</li>
        </ul>
      </div>
      <Checkbox
        label="This Organization is an Integration Partner"
        value={org?.isIntegrator}
        onChange={ev => {
          if (ev.target.checked) {
            onChange({ ...org, isIntegrator: ev.target.checked })
            return
          }
          const text = [
            'Disabling this integration partner will immediately block access to all API keys that are configured ',
            'for usage with this integration. The business implications are potentially severe.\n\n',
            'Are you sure you want to disable this integration?\n\n',
            '(changes will not take effect until you save)',
          ].join('')
          if (window.confirm(text)) {
            onChange({ ...org, isIntegrator: ev.target.checked })
          }
        }}
      />
      <Checkbox
        label="Accept Prescreened Loan Proposals (for Integrators only)"
        value={org?.acceptPrescreen}
        disabled={!org?.isIntegrator}
        onChange={ev => onChange({ ...org, acceptPrescreen: ev.target.checked })}
      />
    </div>
    <div className="form-group">
      <EnabledIntegrations
        enabledIntegrations={org?.enabledIntegrations}
        onChange={enabledIntegrations => onChange({
          ...org,
          enabledIntegrations
        })}
      />
    </div>
  </>
)

const OrgAccessManagementSection = ({ org, onChange }: OrgFormSectionProps) => (
  <div className="form-group">
    <h4>Access Management</h4>
    <label>Installer is permitted to send new referrals</label>
    <div>
      <InputRadio
        id="is-not-on-hold"
        name="isOnHold"
        checked={org?.isOnHold === false}
        onChange={() => onChange({ ...org, isOnHold: false })}
        label="Yes"
      />
      <InputRadio
        id="is-on-hold"
        name="isOnHold"
        checked={org?.isOnHold === true}
        onChange={() => onChange({ ...org, isOnHold: true })}
        label="No"
      />
    </div>
  </div>
)

// const OrgPaymentSection = ({ org, onChange }: OrgFormSectionProps) => (
//   <>
//     <h4 className="mt-3">ACH Payment Information</h4>
//     <div className="form-group">
//       <label>Institution Name</label>
//       <TextInput
//         value={org?.achInstitutionName || ''}
//         onChange={v => onChange({ ...org, achInstitutionName: v })}
//       />
//     </div>
//     <div className="form-group">
//       <label>Routing Number</label>
//       <RoutingNumberInput
//         value={org?.achRoutingNo || ''}
//         onChange={v => onChange({ ...org, achRoutingNo: v })}
//       />
//     </div>
//     <div className="form-group">
//       <label>Account Number</label>
//       <PasswordInput
//         value={org?.achAccountNo || ''}
//         onChange={v => onChange({ ...org, achAccountNo: v })}
//       />
//     </div>
//     <div className="form-group">
//       <label>Account Type</label>
//       <select
//         value={org?.achAccountType}
//         className="form-select"
//         onChange={ev => onChange({ ...org, achAccountType: ev.target.value })}
//       >
//         <option></option>
//         <option value="checking">Checking</option>
//         <option value="savings">Savings</option>
//       </select>
//     </div>
//   </>
// )

export const OrgAdmin = ({ installerID }: OrgAdminProps) => {
  const [err, setErr] = useState('')
  const [org, setOrg] = useState<Partial<Org>>(null)
  const [saving, setSaving] = useState(false)
  const { registration: { installer } } = useAppState()
  const { data, fetching, error } = useApi<Partial<Org>>(`/orgs/${installerID}`, [installerID])

  const showPaymentInfo = installer.isAdmin || installer.isSupport

  useEffect(() => {
    if (data) {
      if (!data.operatingStates?.length) {
        data.operatingStates = ['FL']
      }

      setOrg(data)
    }
  }, [data])

  if (fetching) {
    return <Spinner />
  }

  if (error) {
    return <Section>
      <div className="alert alert-danger">{errorToString(error)}</div>
    </Section>
  }

  return <div className="org-admin">
    {installer.isSupport || installer.isAdmin ? <Section>
      <div className="section-title">
        <h3 className="mb-0">Installer Org Settings</h3>
        {err ? <div className="alert alert-danger">{err}</div> : null}
      </div>

      <OrgBasicInformationSection org={org} onChange={setOrg} />
      <OrgFundingScheduleSection org={org} onChange={setOrg} />
      <OrgIntegrationSection org={org} onChange={setOrg} />
      {/* <OrgAccessManagementSection org={org} onChange={setOrg} />      {showPaymentInfo ? <OrgPaymentSection org={org} onChange={setOrg} /> : null} */}

      <div className="buttons">
        <button className="btn btn-primary"
          disabled={saving}
          onClick={ev => {
            setSaving(true)
            api.patch(`/orgs/${installerID}`, org)
              .then(_org => { toast.success('Org Settings Updated'); setOrg(_org) })
              .catch(ex => { setErr(errorToString(ex)) })
              .finally(() => { setSaving(false) })
          }}
        >{saving ? <Spinner /> : 'Save Org Settings'}</button>
      </div>
    </Section> : null}
    {installer.isAdmin && org?.apiEnabled ? <Section>
      <ApiKeys org={org as Org} />
    </Section> : null}
  </div>
}

export default OrgAdmin
